import { LASLoader } from '@loaders.gl/las';
import type { PointCloudData } from '../../types';

/** Minimal inline interface for loaders.gl LAS mesh output (avoids deep dist import). */
interface LASMesh {
  attributes: {
    POSITION?: { value: Float32Array };
    intensity?: { value: Uint16Array };
    classification?: { value: Uint8Array };
    COLOR_0?: { value: Uint8Array };
  };
  header?: {
    vertexCount?: number;
    boundingBox?: [[number, number, number], [number, number, number]];
  };
}

/**
 * Parse a LAS or LAZ file (ArrayBuffer) into PointCloudData.
 *
 * Uses loaders.gl LAZPerfLoader which handles both .las and .laz
 * synchronously without Web Workers.
 */
export function parseLas(arrayBuffer: ArrayBuffer): PointCloudData {
  const mesh = LASLoader.parseSync(arrayBuffer, {
    las: {
      shape: 'mesh',
      fp64: false,
      skip: 1,
      colorDepth: 'auto',
    },
  }) as LASMesh;

  const positions = mesh.attributes.POSITION?.value as Float32Array;
  const intensityAttr = mesh.attributes.intensity?.value as Uint16Array | undefined;
  const classificationAttr = mesh.attributes.classification?.value as Uint8Array | undefined;
  const colorAttr = mesh.attributes.COLOR_0?.value as Uint8Array | undefined;

  const pointCount = mesh.header?.vertexCount ?? positions.length / 3;

  // Convert intensities Uint16Array → Float32Array (normalized 0-1)
  let intensities: Float32Array;
  if (intensityAttr && intensityAttr.length >= pointCount) {
    intensities = new Float32Array(pointCount);
    let maxI = 0;
    for (let i = 0; i < pointCount; i++) {
      const v = intensityAttr[i] ?? 0;
      if (v > maxI) maxI = v;
      intensities[i] = v;
    }
    if (maxI > 0) {
      for (let i = 0; i < pointCount; i++) {
        intensities[i] /= maxI;
      }
    }
  } else {
    intensities = new Float32Array(pointCount);
  }

  // Classifications — fall back to unclassified (0)
  let classifications: Uint8Array;
  if (classificationAttr && classificationAttr.length >= pointCount) {
    classifications = classificationAttr.slice(0, pointCount);
  } else {
    classifications = new Uint8Array(pointCount);
  }

  // Colors — convert Uint8 RGB(A) → Float32 RGB (0-1 range)
  const colors = new Float32Array(pointCount * 3);
  if (colorAttr && colorAttr.length >= pointCount * 3) {
    const hasAlpha = colorAttr.length >= pointCount * 4;
    const colorSize = hasAlpha ? 4 : 3;
    for (let i = 0; i < pointCount; i++) {
      colors[i * 3] = colorAttr[i * colorSize] / 255;
      colors[i * 3 + 1] = colorAttr[i * colorSize + 1] / 255;
      colors[i * 3 + 2] = colorAttr[i * colorSize + 2] / 255;
    }
  } else {
    // Default grey
    for (let i = 0; i < pointCount; i++) {
      colors[i * 3] = 0.5;
      colors[i * 3 + 1] = 0.5;
      colors[i * 3 + 2] = 0.5;
    }
  }

  // Bounding box from header or compute from positions
  const headerBb = mesh.header?.boundingBox;
  let boundingBox: PointCloudData['boundingBox'];
  if (headerBb && headerBb.length === 2) {
    boundingBox = {
      min: { x: headerBb[0][0], y: headerBb[0][1], z: headerBb[0][2] },
      max: { x: headerBb[1][0], y: headerBb[1][1], z: headerBb[1][2] },
    };
  } else if (pointCount === 0) {
    boundingBox = {
      min: { x: 0, y: 0, z: 0 },
      max: { x: 0, y: 0, z: 0 },
    };
  } else {
    // Compute from positions
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < pointCount; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (z < minZ) minZ = z;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      if (z > maxZ) maxZ = z;
    }
    boundingBox = {
      min: { x: minX, y: minY, z: minZ },
      max: { x: maxX, y: maxY, z: maxZ },
    };
  }

  return {
    positions: positions.slice(0, pointCount * 3),
    colors,
    intensities,
    classifications,
    boundingBox,
    pointCount,
  };
}
